<template>
<default>
<back-nav to="SignIn" :pageChange="true"/>
<div>
  <v-container>
    <v-row no-gutters>
      <v-col
        lg="3"
        offset-lg="4"
        md="6"
        offset-md="3"
        sm="9"
        offset-sm="2"
        align-self="center">
        <v-card
          :loading="loading"
          class="tw-mx-2 tw-my-2"
          min-width="400"
        >
          <template v-slot:loader="{ isActive }">
            <v-progress-linear
              :active="isActive"
              height="4"
              color="deep-purple-accent-4"
              indeterminate
            >
            </v-progress-linear>
          </template>

          <div class="tw-w-full tw-flex tw-justify-center">
            <img
              src="/apalife-logo.svg"
              class="tw-w-24 md:tw-w-32 tw-mt-4 md:tw-mt-6"
              alt="logo"
            />
          </div>

          <v-card-title class="primary--text tw-font-bold tw-mt-2 tw-text-center">
            Set Up Your Password
          </v-card-title>

          <v-card-subtitle class="gray--text tw-my-2">
            Create a secure password for your account
          </v-card-subtitle>

          <v-card-text>
            <div>
              <v-form v-model="isValid" ref="form" @submit.prevent="setupPassword">
                <v-text-field
                  id="username"
                  label="Username/Email"
                  prepend-icon="mdi-account"
                  v-model="username"
                  class="tw-mb-3"
                  :rules="[required('Username/Email'), emailRule]"
                  :error-messages="errors.username"
                  placeholder="Enter your username or email"
                >
                </v-text-field>

                <v-text-field
                  id="resetCode"
                  label="Verification Code"
                  prepend-icon="mdi-key"
                  v-model="resetCode"
                  class="tw-mb-3"
                  :rules="[required('Verification Code'), numericRule]"
                  :error-messages="errors.resetCode"
                  placeholder="Enter the code from your email"
                >
                </v-text-field>

                <v-text-field
                  id="password"
                  label="New Password"
                  :type="showPassword ? 'text' : 'password'"
                  prepend-icon="mdi-lock-reset"
                  :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  v-model="password"
                  class="tw-mb-3"
                  :rules="[required('Password'), passwordValidationRule]"
                  :error-messages="errors.password"
                  @click:append="showPassword = !showPassword"
                  @input="validatePassword"
                  placeholder="Enter your new password"
                >
                </v-text-field>

                <!-- Password Strength Indicator -->
                <div v-if="password" class="tw-mb-4">
                  <v-progress-linear
                    :value="passwordStrengthPercent"
                    :color="passwordStrength.class"
                    height="6"
                    rounded
                  ></v-progress-linear>
                  <div class="tw-text-sm tw-mt-1" :class="`${passwordStrength.class}--text`">
                    {{ passwordStrength.text }}
                  </div>
                </div>

                <v-text-field
                  id="confirmPassword"
                  label="Confirm Password"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  prepend-icon="mdi-lock-reset"
                  :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  v-model="confirmPassword"
                  class="tw-mb-3"
                  :rules="[required('Confirm Password'), confirmPasswordRule]"
                  :error-messages="errors.confirmPassword"
                  @click:append="showConfirmPassword = !showConfirmPassword"
                  @input="validateConfirmPassword"
                  placeholder="Confirm your new password"
                >
                </v-text-field>

                <!-- Password Requirements -->
                <v-card outlined class="tw-mb-4">
                  <v-card-subtitle class="tw-pb-2">
                    <strong>Password Requirements:</strong>
                  </v-card-subtitle>
                  <v-card-text class="tw-pt-0">
                    <v-list dense>
                      <v-list-item
                        v-for="(requirement, key) in passwordRequirements"
                        :key="key"
                        class="tw-px-0 tw-py-1"
                      >
                        <v-list-item-icon class="tw-mr-2">
                          <v-icon
                            small
                            :color="validations[key] ? 'success' : 'error'"
                          >
                            {{ validations[key] ? 'mdi-check' : 'mdi-close' }}
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title
                            class="tw-text-sm"
                            :class="validations[key] ? 'success--text' : 'error--text'"
                          >
                            {{ requirement }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-card-text>
                </v-card>

              </v-form>
            </div>
          </v-card-text>

          <v-card-actions>
            <v-btn
              id="setupPassword"
              class="white--text"
              color="primary"
              :disabled="!isFormValid || loading"
              @click="setupPassword"
              block
            >
              <v-icon left v-if="!loading">mdi-shield-lock</v-icon>
              <v-progress-circular
                v-if="loading"
                indeterminate
                size="20"
                class="tw-mr-2"
              ></v-progress-circular>
              {{ loading ? 'Setting up password...' : 'Set Password' }}
            </v-btn>
          </v-card-actions>

          <v-card-text class="tw-text-center tw-text-sm gray--text">
            <v-icon small color="primary" class="tw-mr-1">mdi-information</v-icon>
            After setting up your password, you'll be redirected to the login page.
          </v-card-text>

        </v-card>
      </v-col>
    </v-row>
  </v-container>

  <!-- Success Modal -->
  <v-dialog v-model="showSuccessModal" persistent max-width="400">
    <v-card>
      <v-card-text class="tw-text-center tw-py-8">
        <v-icon size="64" color="success" class="tw-mb-4">mdi-check-circle</v-icon>
        <h3 class="tw-mb-4">Password Set Successfully!</h3>
        <p class="tw-mb-4">Your password has been created. You will be redirected to the login page.</p>
        <div class="primary--text tw-font-bold">
          Redirecting in {{ countdown }} seconds...
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>

  <!-- Snackbar for notifications -->
  <v-snackbar v-model="snackbar" :color="snackbarType" top>
    {{ snackbarText }}
    <template v-slot:action="{ attrs }">
      <v-btn
        color="white"
        text
        v-bind="attrs"
        @click="snackbar = false"
      >
        Close
      </v-btn>
    </template>
  </v-snackbar>

</div>
</default>
</template>

<script>
import axios from 'axios';
import Default from '../../layouts/Default.vue';
import BackNav from '../../components/BackNav.vue';
import validations from '../../utils/validations';
import { PASSWORD_VALIDATION_MESSAGES } from '../../utils/const';

export default {
  name: 'PasswordSetup',
  components: { Default, BackNav },
  data() {
    return {
      username: '',
      resetCode: '',
      password: '',
      confirmPassword: '',
      loading: false,
      showPassword: false,
      showConfirmPassword: false,
      showSuccessModal: false,
      countdown: 3,
      isValid: false,
      snackbar: false,
      snackbarText: '',
      snackbarType: 'success',
      errors: {
        username: '',
        resetCode: '',
        password: '',
        confirmPassword: '',
      },
      validations: {
        minLength: false,
        hasUppercase: false,
        hasLowercase: false,
        hasNumber: false,
        hasSpecialChar: false,
      },
      passwordRequirements: {
        minLength: 'At least 8 characters long',
        hasUppercase: 'Contains uppercase letter (A-Z)',
        hasLowercase: 'Contains lowercase letter (a-z)',
        hasNumber: 'Contains at least one number (0-9)',
        hasSpecialChar: 'Contains special character (!@#$%^&*)',
      },
      required: validations.required,
    };
  },
  computed: {
    passwordStrength() {
      const validCount = Object.values(this.validations).filter(Boolean).length;

      if (validCount === 0) {
        return { class: 'error', width: '0%', text: '' };
      }
      if (validCount <= 2) {
        return { class: 'error', width: '25%', text: 'Weak' };
      }
      if (validCount <= 3) {
        return { class: 'warning', width: '50%', text: 'Medium' };
      }
      if (validCount <= 4) {
        return { class: 'info', width: '75%', text: 'Good' };
      }
      return { class: 'success', width: '100%', text: 'Strong' };
    },
    passwordStrengthPercent() {
      const validCount = Object.values(this.validations).filter(Boolean).length;
      return (validCount / 5) * 100;
    },
    isFormValid() {
      return !!(
        this.username
        && this.resetCode
        && this.password
        && this.confirmPassword
        && this.password === this.confirmPassword
        && Object.values(this.validations).every(Boolean)
        && !Object.values(this.errors).some(Boolean)
      );
    },
    // Vuetify validation rules
    numericRule() {
      return (value) => {
        if (!value) return PASSWORD_VALIDATION_MESSAGES.VERIFICATION_CODE_REQUIRED;
        if (!/^\d+$/.test(value)) return PASSWORD_VALIDATION_MESSAGES.VERIFICATION_CODE_NUMERIC;
        return true;
      };
    },
    passwordValidationRule() {
      return (value) => {
        if (!value) return PASSWORD_VALIDATION_MESSAGES.PASSWORD_REQUIRED;
        if (!Object.values(this.validations).every(Boolean)) {
          return PASSWORD_VALIDATION_MESSAGES.PASSWORD_REQUIREMENTS_NOT_MET;
        }
        return true;
      };
    },
    confirmPasswordRule() {
      return (value) => {
        if (!value) return PASSWORD_VALIDATION_MESSAGES.CONFIRM_PASSWORD_REQUIRED;
        if (value !== this.password) return PASSWORD_VALIDATION_MESSAGES.PASSWORDS_DO_NOT_MATCH;
        return true;
      };
    },
    emailRule() {
      return (value) => {
        if (!value) return 'Username/Email is required';
        // Safe email validation with limited quantifiers to prevent ReDoS
        const emailPattern = /^[a-zA-Z0-9._-]{1,64}@[a-zA-Z0-9.-]{1,64}\.[a-zA-Z]{2,}$/;
        if (value.includes('@') && !emailPattern.test(value)) {
          return 'Please enter a valid email address';
        }
        return true;
      };
    },
  },
  mounted() {
    // Optionally still extract params if provided, but don't require them
    this.extractParamsFromUrl();
  },
  methods: {
    extractParamsFromUrl() {
      const params = new URLSearchParams(window.location.search);
      // Pre-fill fields if URL parameters are provided, but don't require them
      this.username = params.get('username') || '';
      this.resetCode = params.get('resetCode') || '';
    },

    validatePassword() {
      this.validations.minLength = this.password.length >= 8;
      this.validations.hasUppercase = /[A-Z]/.test(this.password);
      this.validations.hasLowercase = /[a-z]/.test(this.password);
      this.validations.hasNumber = /\d/.test(this.password);
      this.validations.hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(this.password);

      // Clear password error if requirements are met
      if (Object.values(this.validations).every(Boolean)) {
        this.errors.password = '';
      }

      // Re-validate confirm password if it exists
      if (this.confirmPassword) {
        this.validateConfirmPassword();
      }
    },

    validateConfirmPassword() {
      if (this.password !== this.confirmPassword) {
        this.errors.confirmPassword = PASSWORD_VALIDATION_MESSAGES.PASSWORDS_DO_NOT_MATCH;
      } else {
        this.errors.confirmPassword = '';
      }
    },

    async setupPassword() {
      if (!this.validateForm()) {
        return;
      }

      this.loading = true;

      try {
        const payload = {
          username: this.username,
          resetCode: parseInt(this.resetCode, 10),
          password: this.password,
        };

        const response = await axios.post('/lmsagent/login/resetUserPassword', payload);

        if (response.status === 200 || response.status === 201) {
          this.handleSuccess();
        } else {
          throw new Error('Unexpected response status');
        }
      } catch (error) {
        this.handleError(error);
      } finally {
        this.loading = false;
      }
    },

    validateForm() {
      let isValid = true;

      // Reset errors
      this.errors = {
        username: '',
        resetCode: '',
        password: '',
        confirmPassword: '',
      };

      // Validate username
      if (!this.username) {
        this.errors.username = 'Username/Email is required';
        isValid = false;
      } else if (this.username.includes('@')) {
        const emailPattern = /^[a-zA-Z0-9._-]{1,64}@[a-zA-Z0-9.-]{1,64}\.[a-zA-Z]{2,}$/;
        if (!emailPattern.test(this.username)) {
          this.errors.username = 'Please enter a valid email address';
          isValid = false;
        }
      }

      // Validate reset code
      if (!this.resetCode) {
        this.errors.resetCode = PASSWORD_VALIDATION_MESSAGES.VERIFICATION_CODE_REQUIRED;
        isValid = false;
      } else if (!/^\d+$/.test(this.resetCode)) {
        this.errors.resetCode = PASSWORD_VALIDATION_MESSAGES.VERIFICATION_CODE_NUMERIC;
        isValid = false;
      }

      // Validate password
      if (!Object.values(this.validations).every(Boolean)) {
        this.errors.password = PASSWORD_VALIDATION_MESSAGES.PASSWORD_REQUIREMENTS_NOT_MET;
        isValid = false;
      }

      // Validate confirm password
      if (this.password !== this.confirmPassword) {
        this.errors.confirmPassword = PASSWORD_VALIDATION_MESSAGES.PASSWORDS_DO_NOT_MATCH;
        isValid = false;
      }

      return isValid;
    },

    handleSuccess() {
      this.showSuccessModal = true;
      this.startCountdown();
    },

    startCountdown() {
      const timer = setInterval(() => {
        this.countdown -= 1;
        if (this.countdown <= 0) {
          clearInterval(timer);
          this.$router.push('/');
        }
      }, 1000);
      return timer;
    },

    handleError(error) {
      let errorMessage = 'Failed to set up password. Please try again.';

      if (error.response?.data) {
        const apiError = error.response.data;
        if (typeof apiError === 'string') {
          errorMessage = apiError;
        } else if (apiError.message) {
          errorMessage = apiError.message;
        }

        // Handle specific error cases
        if (errorMessage.includes('Invalid reset code') || errorMessage.includes('expired')) {
          errorMessage = 'The verification code is invalid or has expired. Please request a new password setup link.';
        } else if (errorMessage.includes('User not found')) {
          errorMessage = 'User account not found. Please contact your administrator.';
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your network connection.';
      }

      this.showMessage('error', errorMessage);
    },

    showMessage(type, message) {
      this.snackbarText = message;
      this.snackbarType = type === 'error' ? 'error' : 'success';
      this.snackbar = true;
    },
  },
};
</script>
